import type * as zod from 'zod';

import { inspectCountVariableKey } from '@sb/ai/types/inspectClassKeys';
import {
  INSPECT_THRESHOLDING_CAPS,
  inspectThresholdFloor,
} from '@sb/ai/types/inspectThresholds';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { getCameraColorFrame } from '@sb/routine-runner/vision/helpers';
import { countInspectDetections } from '@sb/routine-runner/vision/inspectCounts';
import type { LocateMatch } from '@sb/routine-runner/vision/LocateTypes';
import { blobClassName } from '@sb/routine-runner/vision/LocateTypes';
import { callRunRoboflowDetection } from '@sb/routine-runner/vision/runLocate';

import type { StepFailure } from '../../types';
import { captureFrameToStorage } from '../Capture/captureFrameToStorage';
import type { ImageValidationFailure } from '../Capture/captureUtils';
import type { FrameName } from '../Capture/fileNameParts';
import { nameFrame, readFileNameParts } from '../Capture/fileNameParts';
import { makeCaptureStorageNotifier } from '../Capture/notifyCaptureStorage';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

const log = makeNamespacedLog('runInspectSkill');

/** The workflow input a frame's name goes to. */
const CAPTURE_NAME_INPUT = 'capture_name';

/** vision-bot sends the frame as this input, which a parameter would replace. */
const FRAME_INPUT = 'image';

export default class RunInspectSkillStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    // No counts, because the class list lives on the skill and this runs
    // synchronously before the skill is resolved. Until the first play, an `If`
    // on count_<id> reads an undefined variable — the same thing it reads for
    // any step that has not run yet. The capture pair starts null, matching
    // Locate, so a routine reading them before a play gets a null instead of an
    // absent key.
    this.variables = {
      captureTimestamp: null,
      latestSaveFilepath: null,
    };
  }

  /**
   * The envelope every `_play` failure shares, bar the missing-camera guard —
   * that one reports `CameraFailure` rather than a step-play failure.
   */
  private playFailure(reason: string, error?: Error): StepFailure {
    return {
      failure: {
        kind: FailureKind.StepPlayFailure,
        stepKind: this.getStepKind(),
      },
      failureReason: `RunInspectSkill: ${reason}`,
      error,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      log.info('play', 'Playing RunInspectSkill step', {
        skillId: this.args.skillId,
      });

      const skill = await this.routineContext.getInspectSkillById(
        this.args.skillId,
      );

      const { shared } = skill;

      const cameraEquipment = this.routineContext.equipment.getDeviceById(
        shared.cameraId,
      ) as Camera | undefined;

      if (!cameraEquipment) {
        return fail({
          failure: { kind: FailureKind.CameraFailure },
          failureReason: `RunInspectSkill: camera equipment with id ${shared.cameraId} not found`,
        });
      }

      // No runner is a valid configuration, not a missing one. A GPU task
      // runner IS a vision-bot — a remote one — so the robot's own serves the
      // same workflow, more slowly.
      //
      // A configured one still has to be there and healthy, and is checked
      // BEFORE the frame rather than after: the workflow was preloaded onto
      // that runner's inference container, so a disconnected vision bot cannot
      // be recovered by retrying the detection, and a frame captured first is
      // paid for and thrown away.
      //
      // `updateAsync`, not the synchronous getter: that hands back the last
      // background poll, which is up to its 10s interval old — long enough for
      // a runner that dropped since to read as healthy here and fail inside the
      // detection instead, where the message says nothing about the vision bot.
      if (shared.gpuTaskRunnerId) {
        const gpuTaskRunner = this.routineContext.equipment.getDeviceById(
          shared.gpuTaskRunnerId,
        ) as GPUTaskRunner | undefined;

        if (!gpuTaskRunner) {
          return fail(
            this.playFailure(
              `GPU Task Runner equipment with id ${shared.gpuTaskRunnerId} not found`,
            ),
          );
        }

        if (!(await gpuTaskRunner.updateAsync()).isVisionBotConnected) {
          return fail(
            this.playFailure(
              `GPU Task Runner '${shared.gpuTaskRunnerId}' is not healthy (vision bot disconnected)`,
            ),
          );
        }
      }

      // Before the frame, so a missing variable costs no capture.
      const fileNameParts = readFileNameParts(
        this.args.captureConfig?.fileName,
        this,
      );

      // Skills are camera-bot-cpp only, so there is no legacy CameraIntegration
      // to pass — the same call Locate's skill path makes.
      const image = await getCameraColorFrame(cameraEquipment, undefined);

      // Before the run, so `capture_name` is the frame's generated name.
      const { name: captureName, frameName } = nameFrame({
        stepKind: 'RunInspectSkill',
        skillId: this.args.skillId,
        fileNameParts,
      });

      // The ROI crops before inference so the workflow never sees pixels the
      // operator excluded. `preprocessImage` also reports the crop's offset,
      // which this step deliberately discards: the boxes it records are in the
      // INSPECTED image's space, and the inspected image is what gets saved
      // beside them, so nothing here needs translating back to full-frame
      // coordinates. Saving the full frame instead would need that offset added
      // to every box, and a record whose boxes miss the pixels by the crop
      // origin reads as a working record.
      const crop = shared.regionOfInterest
        ? await this.routineContext.vision.preprocessImage(
            image,
            shared.regionOfInterest,
          )
        : null;

      const inspectedImage = crop ? crop.croppedImage : image;

      const { matches, activeLearningStatus } = await callRunRoboflowDetection({
        equipment: this.routineContext.equipment,
        vision: this.routineContext.vision,
        gpuTaskRunnerId: shared.gpuTaskRunnerId,
        base64Image: Buffer.from(inspectedImage).toString('base64'),
        workspaceName: shared.roboflowWorkspaceName,
        workflowId: shared.roboflowWorkflowId,
        outputName: shared.roboflowOutputName,
        thresholdingParams: {
          // The workflow's OWN floor, not the lowest per-class threshold.
          // Per-class filtering happens below, after a raw inference, so the
          // strictest per-class value here would narrow the set twice — and any
          // non-zero value would drop a class republished into the workflow
          // since import before it could be counted or reported.
          confidence_threshold: String(
            inspectThresholdFloor(shared.roboflowConfidenceFloor),
          ),
          // The cap and the per-class flag come from one place, so this and
          // the wizard's preview cannot drift apart — see
          // `INSPECT_THRESHOLDING_CAPS` for why that mattered.
          ...INSPECT_THRESHOLDING_CAPS,
        },
        // A workflow ignores inputs it does not declare. An empty value is left
        // out, so the workflow's default applies.
        workflowInputs: {
          ...Object.fromEntries(
            Object.entries(fileNameParts.nameValues).filter(
              ([key, value]) => value !== '' && key !== FRAME_INPUT,
            ),
          ),
          [CAPTURE_NAME_INPUT]: captureName,
          // Last, so the inputs stored at import, which keep the workflow's own
          // filtering off, win over a variable of the same name.
          ...shared.roboflowWorkflowInputs,
        },
      });

      const { countsByClassId, unlistedClassNames, unattributedCount } =
        countInspectDetections({
          matches,
          classes: shared.classes,
          workflowFloor: shared.roboflowConfidenceFloor,
        });

      // The only detections this step saw and did not count. Logged rather than
      // failed: both mean the model emitted something the imported class list
      // does not describe, which a workflow edited in Roboflow after import will
      // do, and halting a line over that is worse than a count an operator can
      // reconcile against this line. No validation rule can catch it — the
      // step's classes and the skill's classes agree with each other and both
      // disagree with the model.
      if (unlistedClassNames.length > 0 || unattributedCount > 0) {
        log.warn('play', 'Inspect detections were not counted', {
          skillId: this.args.skillId,
          workflow: `${shared.roboflowWorkspaceName}/${shared.roboflowWorkflowId}#${shared.roboflowOutputName}`,
          unlistedClassNames,
          unattributedCount,
        });
      }

      // Wholesale, so a class dropped from the skill since the last play stops
      // publishing a count instead of keeping a stale one. The capture pair is
      // reset here for the same reason and refilled below: `setVariable` merges
      // onto this, so the capture has to come AFTER this assignment or the
      // counts would overwrite the path the capture just published.
      const counts: Variables = {
        captureTimestamp: null,
        latestSaveFilepath: null,
      };

      for (const [id, count] of Object.entries(countsByClassId)) {
        counts[inspectCountVariableKey(id)] = count;
      }

      this.variables = counts;

      const captureFailure = await this.maybeCaptureImage(
        inspectedImage,
        matches,
        countsByClassId,
        {
          workflowId: shared.roboflowWorkflowId,
          outputName: shared.roboflowOutputName,
          activeLearningStatus,
        },
        frameName,
      );

      if (captureFailure) {
        return fail(captureFailure);
      }

      log.info('play', 'RunInspectSkill step completed', {
        skillId: this.args.skillId,
        matchCount: matches.length,
      });
    } catch (error) {
      return fail(this.playFailure((error as Error).message, error as Error));
    }
  }

  /**
   * If `captureConfig` is set, writes the inspected frame to storage with its
   * defect record beside it.
   *
   * The record's detections are the ones this step SAW, including any whose
   * class the skill does not list, while the counts cover only the listed
   * classes — so a reader comparing them can tell an unlisted class apart from
   * a clean part, which the step otherwise only logs.
   */
  private async maybeCaptureImage(
    image: ArrayBuffer,
    matches: LocateMatch[],
    countsByClassId: Record<string, number>,
    /**
     * Which workflow and output judged this frame. Passed in rather than read
     * from `this.args`, because the step resolves the skill fresh on every play
     * and the skill is where these live — so the record names the model that
     * actually ran, not one a routine was authored against.
     */
    model: {
      workflowId: string;
      outputName: string;
      activeLearningStatus?: string;
    },
    frameName: FrameName,
  ): Promise<ImageValidationFailure | null> {
    const { captureConfig } = this.args;

    if (!captureConfig) {
      return null;
    }

    const { captureConfig: _captureConfig, ...stepParameters } = this.args;

    return captureFrameToStorage({
      image,
      captureConfig,
      stepKind: 'RunInspectSkill',
      stepParameters,
      robotId: this.routineContext.robotId,
      routineId: this.routineFrame.routineId,
      ...frameName,
      reportStoragePressure: makeCaptureStorageNotifier(this.routineContext),
      skillId: this.args.skillId,
      results: {
        inspectResults: {
          detections: matches.map((match) => ({
            // The shared resolver, so the class a record names is the class the
            // overlay coloured and the count counted.
            className: blobClassName(match),
            score: match.detection.score,
            x: match.detection.x,
            y: match.detection.y,
            width: match.detection.width,
            height: match.detection.height,
          })),
          countsByClassId,
          ...model,
        },
      },
      evaluateExpression: (expression) =>
        this.routineFrame.evaluateExpression(expression),
      getDeviceById: (id) => this.routineContext.equipment.getDeviceById(id),
      publishVariable: (name, value) => this.setVariable(name, value),
    });
  }
}
